import { DownOutlined, MenuOutlined } from '@ant-design/icons';
import type { MenuProps } from 'antd';
import { Dropdown } from 'antd';
import type { NextPage } from 'next';
import dynamic from 'next/dynamic';
import Image from 'next/image';
import Link from 'next/link';
import {
  type CSSProperties,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { RightContext } from '@/components/RightProvider/context';
import { LandingBridgeProvider } from '@/landing/bridge';
import SEOHead from '@/landing/components/SEOHead';
import { SEO_CONFIG, VIDEO_SHOWCASE_VIDEOS } from '@/landing/e-commerce/config';
import {
  aiToolURL,
  imageGenerateURL,
  pricingURL,
  productAvatarURL,
  viralTemplateURL,
} from '@/utils/routeConstants';

import { footerSections, type HeroVideoItem, heroVideos, HOME_CTA_URL } from './config';
import { NEW_HOME_FEATURE_GROUPS, NEW_HOME_USE_CASE_LINKS } from './header-nav';
import styles from './index.module.scss';
import { mapTemplateVideoItemsToHeroVideos } from './mapTemplateVideosFromApi';
import { ArrowIcon, HeroAIToolsStrip, HeroMarqueeVideoCard } from './shared';
import {
  NewHomeTemplateListProvider,
  useSharedTemplateList,
} from './template-list-context';

const LogosSection = dynamic(() =>
  import('./deferred-sections').then((mod) => mod.LogosSection),
);
const TemplatesSection = dynamic(() =>
  import('./deferred-sections').then((mod) => mod.TemplatesSection),
);
const ToolsTicker = dynamic(() =>
  import('./deferred-sections').then((mod) => mod.ToolsTicker),
);
const StepsSection = dynamic(() =>
  import('./deferred-sections').then((mod) => mod.StepsSection),
);
const FeatureBlocks = dynamic(() =>
  import('./deferred-sections').then((mod) => mod.FeatureBlocks),
);
const AvatarShowcase = dynamic(() =>
  import('./deferred-sections').then((mod) => mod.AvatarShowcase),
);
const ToolCardsSection = dynamic(() =>
  import('./deferred-sections').then((mod) => mod.ToolCardsSection),
);
const TestimonialsSection = dynamic(() =>
  import('./deferred-sections').then((mod) => mod.TestimonialsSection),
);
const FAQSection = dynamic(() =>
  import('./deferred-sections').then((mod) => mod.FAQSection),
);

export const heroTabs = [
  {
    label: 'Template',
    href: viralTemplateURL,
    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
      >
        <path
          d="M10.6666 8.66664L14.1486 10.988C14.1988 11.0214 14.2571 11.0405 14.3174 11.0434C14.3776 11.0463 14.4375 11.0328 14.4906 11.0043C14.5438 10.9759 14.5882 10.9335 14.6192 10.8818C14.6502 10.8301 14.6666 10.7709 14.6666 10.7106V5.24664C14.6666 5.18799 14.6512 5.13037 14.6218 5.07961C14.5924 5.02884 14.5502 4.98672 14.4993 4.95751C14.4485 4.92829 14.3908 4.91301 14.3322 4.91321C14.2735 4.91341 14.2159 4.92908 14.1653 4.95864L10.6666 6.99997"
          stroke="white"
          stroke-width="1.33333"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
        <path
          d="M9.33337 4H2.66671C1.93033 4 1.33337 4.59695 1.33337 5.33333V10.6667C1.33337 11.403 1.93033 12 2.66671 12H9.33337C10.0698 12 10.6667 11.403 10.6667 10.6667V5.33333C10.6667 4.59695 10.0698 4 9.33337 4Z"
          stroke="white"
          stroke-width="1.33333"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    ),
  },
  {
    label: 'AI Avatar',
    href: productAvatarURL,
    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
      >
        <path
          d="M10.6667 14V12.6667C10.6667 11.9594 10.3858 11.2811 9.88566 10.781C9.38556 10.281 8.70728 10 8.00004 10H4.00004C3.2928 10 2.61452 10.281 2.11442 10.781C1.61433 11.2811 1.33337 11.9594 1.33337 12.6667V14"
          stroke="#D1D5DC"
          stroke-width="1.33333"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
        <path
          d="M6.00004 7.33333C7.4728 7.33333 8.66671 6.13943 8.66671 4.66667C8.66671 3.19391 7.4728 2 6.00004 2C4.52728 2 3.33337 3.19391 3.33337 4.66667C3.33337 6.13943 4.52728 7.33333 6.00004 7.33333Z"
          stroke="#D1D5DC"
          stroke-width="1.33333"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
        <path
          d="M14.6666 14V12.6667C14.6662 12.0758 14.4695 11.5019 14.1075 11.0349C13.7455 10.5679 13.2387 10.2344 12.6666 10.0867"
          stroke="#D1D5DC"
          stroke-width="1.33333"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
        <path
          d="M10.6666 2.08667C11.2402 2.23354 11.7487 2.56714 12.1117 3.03488C12.4748 3.50262 12.6719 4.07789 12.6719 4.67C12.6719 5.26212 12.4748 5.83739 12.1117 6.30513C11.7487 6.77287 11.2402 7.10647 10.6666 7.25334"
          stroke="#D1D5DC"
          stroke-width="1.33333"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    ),
  },
  {
    label: 'Agent Generate',
    href: imageGenerateURL,
    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
      >
        <path
          d="M6 3.44995C6.12421 3.44986 6.24479 3.49182 6.34211 3.56899C6.43943 3.64616 6.50777 3.754 6.536 3.87495V3.87595L6.537 3.87895L6.541 3.89595L6.559 3.96795C6.66085 4.35904 6.782 4.74484 6.922 5.12395C7.186 5.83595 7.55 6.59095 7.979 7.02095C8.409 7.45095 9.164 7.81395 9.876 8.07795C10.2551 8.21793 10.6409 8.33907 11.032 8.44095L11.104 8.45895L11.121 8.46295H11.124C11.2451 8.49117 11.353 8.55949 11.4303 8.65681C11.5077 8.75414 11.5498 8.87475 11.5499 8.99905C11.55 9.12336 11.5081 9.24405 11.431 9.34152C11.3538 9.43899 11.246 9.50751 11.125 9.53595H11.124L11.121 9.53695L11.103 9.54095L11.033 9.55895C10.6419 9.66083 10.2561 9.78198 9.877 9.92195C9.164 10.186 8.409 10.55 7.979 10.98C7.549 11.41 7.186 12.164 6.922 12.876C6.78202 13.2551 6.66087 13.6409 6.559 14.032L6.541 14.104L6.537 14.121V14.124C6.50878 14.245 6.44046 14.353 6.34314 14.4303C6.24581 14.5076 6.1252 14.5498 6.0009 14.5499C5.87659 14.55 5.7559 14.5081 5.65843 14.4309C5.56096 14.3538 5.49244 14.246 5.464 14.125V14.124L5.463 14.121L5.459 14.103L5.441 14.033C5.33913 13.6419 5.21798 13.2561 5.078 12.877C4.814 12.164 4.45 11.409 4.021 10.979C3.591 10.549 2.836 10.186 2.124 9.92195C1.74489 9.78195 1.35909 9.6608 0.968001 9.55895L0.896001 9.53995L0.880001 9.53695H0.876001C0.754942 9.50874 0.646981 9.44041 0.569657 9.34309C0.492333 9.24576 0.450189 9.12515 0.450073 9.00085C0.449958 8.87654 0.491876 8.75585 0.569019 8.65838C0.646161 8.56091 0.753995 8.49239 0.875001 8.46395H0.876001L0.879001 8.46295L0.896001 8.45895L0.968001 8.44095C1.35909 8.3391 1.74489 8.21795 2.124 8.07795C2.836 7.81395 3.591 7.44995 4.021 7.02095C4.451 6.59095 4.814 5.83595 5.078 5.12395C5.218 4.74484 5.33915 4.35904 5.441 3.96795L5.459 3.89595L5.463 3.87895V3.87595C5.49124 3.75473 5.55968 3.64664 5.6572 3.56928C5.75471 3.49193 5.87553 3.44987 6 3.44995ZM5.876 6.08795C5.612 6.69295 5.255 7.34095 4.798 7.79795C4.265 8.32995 3.47 8.72795 2.79 8.99995C3.471 9.27295 4.265 9.66995 4.798 10.202C5.331 10.735 5.728 11.529 6 12.21C6.273 11.529 6.67 10.735 7.202 10.202C7.735 9.66995 8.53 9.27195 9.21 8.99995C8.529 8.72695 7.735 8.32995 7.202 7.79795C6.67 7.26495 6.272 6.46995 6 5.78995L5.876 6.08795ZM12 0.449951C12.1242 0.449863 12.2448 0.491818 12.3421 0.568987C12.4394 0.646156 12.5078 0.753996 12.536 0.874951V0.876951L12.538 0.885951L12.548 0.925951L12.591 1.08095C12.629 1.21395 12.685 1.39695 12.759 1.59795C12.916 2.02095 13.122 2.43395 13.344 2.65595C13.566 2.87795 13.979 3.08395 14.402 3.24095C14.6224 3.32238 14.8466 3.3928 15.074 3.45195L15.114 3.46195L15.123 3.46395H15.125C15.2463 3.49185 15.3545 3.56005 15.432 3.6574C15.5095 3.75476 15.5517 3.87551 15.5517 3.99995C15.5517 4.12439 15.5095 4.24515 15.432 4.3425C15.3545 4.43986 15.2463 4.50805 15.125 4.53595H15.123L15.114 4.53795L15.074 4.54795C14.8467 4.60678 14.6224 4.67686 14.402 4.75795C13.979 4.91595 13.566 5.12195 13.344 5.34395C13.122 5.56595 12.916 5.97895 12.759 6.40195C12.6776 6.62234 12.6072 6.84661 12.548 7.07395L12.538 7.11395L12.536 7.12395C12.5082 7.24525 12.4401 7.35354 12.3428 7.43113C12.2456 7.50872 12.1248 7.55103 12.0004 7.55115C11.876 7.55126 11.7552 7.50918 11.6577 7.43177C11.5603 7.35436 11.492 7.24619 11.464 7.12495V7.12295L11.462 7.11395L11.452 7.07395C11.3931 6.84663 11.323 6.62236 11.242 6.40195C11.084 5.97895 10.878 5.56595 10.656 5.34395C10.434 5.12195 10.021 4.91595 9.598 4.75895C9.37764 4.67752 9.15336 4.6071 8.926 4.54795L8.886 4.53795L8.877 4.53595H8.875C8.75373 4.50805 8.64551 4.43986 8.568 4.3425C8.4905 4.24515 8.4483 4.12439 8.4483 3.99995C8.4483 3.87551 8.4905 3.75476 8.568 3.6574C8.64551 3.56005 8.75373 3.49185 8.875 3.46395H8.877L8.886 3.46195L8.926 3.45195L9.081 3.40895C9.214 3.37095 9.397 3.31495 9.598 3.24095C10.021 3.08395 10.434 2.87795 10.656 2.65595C10.878 2.43395 11.084 2.02095 11.241 1.59795C11.3224 1.37756 11.3928 1.15329 11.452 0.925951L11.462 0.885951L11.464 0.876951V0.874951C11.4922 0.753996 11.5606 0.646156 11.6579 0.568987C11.7552 0.491818 11.8758 0.449863 12 0.449951ZM12 2.60995C11.85 2.90595 11.665 3.20495 11.435 3.43495C11.205 3.66495 10.905 3.85095 10.609 3.99995C10.906 4.14995 11.204 4.33495 11.435 4.56495C11.665 4.79495 11.851 5.09395 12 5.38995C12.15 5.09395 12.336 4.79495 12.565 4.56495C12.795 4.33495 13.094 4.14895 13.39 3.99995C13.0873 3.85567 12.8089 3.66504 12.565 3.43495C12.3348 3.19073 12.1442 2.91203 12 2.60895V2.60995Z"
          fill="#D1D5DC"
        />
      </svg>
    ),
  },
  {
    label: 'AI Tools',
    href: aiToolURL,
    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
      >
        <path
          d="M12.6667 2H3.33333C2.59695 2 2 2.59695 2 3.33333V12.6667C2 13.403 2.59695 14 3.33333 14H12.6667C13.403 14 14 13.403 14 12.6667V3.33333C14 2.59695 13.403 2 12.6667 2Z"
          stroke="#D1D5DC"
          stroke-width="1.33333"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
        <path
          d="M5.99996 7.33329C6.73634 7.33329 7.33329 6.73634 7.33329 5.99996C7.33329 5.26358 6.73634 4.66663 5.99996 4.66663C5.26358 4.66663 4.66663 5.26358 4.66663 5.99996C4.66663 6.73634 5.26358 7.33329 5.99996 7.33329Z"
          stroke="#D1D5DC"
          stroke-width="1.33333"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
        <path
          d="M14 9.99996L11.9427 7.94263C11.6926 7.69267 11.3536 7.55225 11 7.55225C10.6464 7.55225 10.3074 7.69267 10.0573 7.94263L4 14"
          stroke="#D1D5DC"
          stroke-width="1.33333"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    ),
  },
];

const AGENT_GENERATE_HERO_ITEMS: HeroVideoItem[] = VIDEO_SHOWCASE_VIDEOS.map((v) => ({
  id: v.id,
  label: v.alt?.trim() || 'AI generated',
  poster: (v.posterUrl || '').trim(),
  videoUrl: v.videoUrl,
}));

const HomeHeader = () => {
  const rightContext = useContext(RightContext);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [expandedMobile, setExpandedMobile] = useState<'features' | 'useCases' | null>(
    null,
  );

  const featureMenuItems = useMemo<MenuProps['items']>(
    () =>
      NEW_HOME_FEATURE_GROUPS.map((group) => ({
        type: 'group' as const,
        label: group.label,
        children: group.links.map((item) => ({
          key: item.key,
          label: (
            <Link href={item.href} className={styles.navDropdownLink}>
              {item.label}
            </Link>
          ),
        })),
      })),
    [],
  );

  const useCaseMenuItems = useMemo<MenuProps['items']>(
    () =>
      NEW_HOME_USE_CASE_LINKS.map((item) => ({
        key: item.key,
        label: (
          <Link href={item.href} className={styles.navDropdownLink}>
            {item.label}
          </Link>
        ),
      })),
    [],
  );

  const closeMobile = () => {
    setMobileOpen(false);
    setExpandedMobile(null);
  };

  return (
    <header className={styles.header}>
      <div className={styles.headerInner}>
        <Link href="/" className={styles.logoLink}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="30"
            height="30"
            viewBox="0 0 30 30"
            fill="none"
          >
            <path
              d="M9.32608 9.17214C9.32608 6.3867 11.5841 4.12866 14.3696 4.12866H29.5C29.5 6.9141 27.242 9.17214 24.4565 9.17214H9.32608Z"
              fill="url(#paint0_linear_5075_52089)"
            />
            <path
              d="M0.5 16.1069C0.5 13.3215 2.75804 11.0634 5.54348 11.0634H20.6739C20.6739 13.8489 18.4159 16.1069 15.6304 16.1069H0.5Z"
              fill="url(#paint1_linear_5075_52089)"
            />
            <path
              d="M11.2978 21.9884C10.6563 24.699 7.93904 26.3761 5.2285 25.7345L7.05956 17.9982H12.2422L11.2978 21.9884Z"
              fill="url(#paint2_linear_5075_52089)"
            />
            <defs>
              <linearGradient
                id="paint0_linear_5075_52089"
                x1="6.61392"
                y1="25.5173"
                x2="24.3434"
                y2="11.273"
                gradientUnits="userSpaceOnUse"
              >
                <stop stopColor="#8F49FF" />
                <stop offset="1" stopColor="#3CC2FF" />
              </linearGradient>
              <linearGradient
                id="paint1_linear_5075_52089"
                x1="6.61392"
                y1="25.5173"
                x2="24.3434"
                y2="11.273"
                gradientUnits="userSpaceOnUse"
              >
                <stop stopColor="#8F49FF" />
                <stop offset="1" stopColor="#3CC2FF" />
              </linearGradient>
              <linearGradient
                id="paint2_linear_5075_52089"
                x1="6.61392"
                y1="25.5173"
                x2="24.3434"
                y2="11.273"
                gradientUnits="userSpaceOnUse"
              >
                <stop stopColor="#8F49FF" />
                <stop offset="1" stopColor="#3CC2FF" />
              </linearGradient>
            </defs>
          </svg>

          <svg
            width="80"
            height="15"
            viewBox="0 0 80 15"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M5.86808e-05 14.46V0.399961H2.72006V14.46H5.86808e-05ZM2.00006 8.69996V6.27996H9.08006V8.69996H2.00006ZM2.00006 2.79996V0.399961H9.42006V2.79996H2.00006ZM15.0905 14.66C14.2105 14.66 13.4172 14.44 12.7105 14C12.0039 13.56 11.4505 12.96 11.0505 12.2C10.6505 11.44 10.4505 10.5866 10.4505 9.63996C10.4505 8.67996 10.6505 7.81996 11.0505 7.05996C11.4505 6.29996 12.0039 5.69996 12.7105 5.25996C13.4172 4.81996 14.2105 4.59996 15.0905 4.59996C15.7839 4.59996 16.4039 4.73996 16.9505 5.01996C17.4972 5.29996 17.9305 5.69329 18.2505 6.19996C18.5839 6.69329 18.7639 7.25329 18.7905 7.87996V11.36C18.7639 12 18.5839 12.5666 18.2505 13.06C17.9305 13.5533 17.4972 13.9466 16.9505 14.24C16.4039 14.52 15.7839 14.66 15.0905 14.66ZM15.5705 12.24C16.3039 12.24 16.8972 12 17.3505 11.52C17.8039 11.0266 18.0305 10.3933 18.0305 9.61996C18.0305 9.11329 17.9239 8.66663 17.7105 8.27996C17.5105 7.87996 17.2239 7.57329 16.8505 7.35996C16.4905 7.13329 16.0639 7.01996 15.5705 7.01996C15.0905 7.01996 14.6639 7.13329 14.2905 7.35996C13.9305 7.57329 13.6439 7.87996 13.4305 8.27996C13.2305 8.66663 13.1305 9.11329 13.1305 9.61996C13.1305 10.14 13.2305 10.6 13.4305 11C13.6439 11.3866 13.9305 11.6933 14.2905 11.92C14.6639 12.1333 15.0905 12.24 15.5705 12.24ZM17.8905 14.46V11.86L18.3105 9.49996L17.8905 7.17996V4.79996H20.4905V14.46H17.8905ZM22.9083 14.46V-4.00543e-05H25.5283V14.46H22.9083ZM32.5097 14.66C31.5231 14.66 30.6364 14.44 29.8497 14C29.0631 13.56 28.4431 12.96 27.9897 12.2C27.5364 11.44 27.3097 10.5866 27.3097 9.63996C27.3097 8.67996 27.5364 7.81996 27.9897 7.05996C28.4431 6.29996 29.0631 5.69996 29.8497 5.25996C30.6497 4.80663 31.5364 4.57996 32.5097 4.57996C33.2697 4.57996 33.9697 4.71996 34.6097 4.99996C35.2497 5.27996 35.8031 5.67996 36.2697 6.19996L34.5897 7.89996C34.3364 7.61996 34.0297 7.40663 33.6697 7.25996C33.3231 7.11329 32.9364 7.03996 32.5097 7.03996C32.0297 7.03996 31.5964 7.15329 31.2097 7.37996C30.8364 7.59329 30.5364 7.89329 30.3097 8.27996C30.0964 8.65329 29.9897 9.09996 29.9897 9.61996C29.9897 10.1266 30.0964 10.58 30.3097 10.98C30.5364 11.3666 30.8364 11.6733 31.2097 11.9C31.5964 12.1133 32.0297 12.22 32.5097 12.22C32.9497 12.22 33.3431 12.1466 33.6897 12C34.0497 11.84 34.3564 11.6133 34.6097 11.32L36.3097 13.02C35.8164 13.5533 35.2497 13.96 34.6097 14.24C33.9697 14.52 33.2697 14.66 32.5097 14.66ZM42.2187 14.66C41.2453 14.66 40.3653 14.44 39.5787 14C38.8053 13.5466 38.1853 12.94 37.7187 12.18C37.2653 11.4066 37.0387 10.5466 37.0387 9.59996C37.0387 8.65329 37.2653 7.80663 37.7187 7.05996C38.172 6.29996 38.792 5.69996 39.5787 5.25996C40.3653 4.80663 41.2387 4.57996 42.1987 4.57996C43.1853 4.57996 44.0653 4.80663 44.8387 5.25996C45.6253 5.69996 46.2453 6.29996 46.6987 7.05996C47.152 7.80663 47.3787 8.65329 47.3787 9.59996C47.3787 10.5466 47.152 11.4066 46.6987 12.18C46.2453 12.94 45.6253 13.5466 44.8387 14C44.0653 14.44 43.192 14.66 42.2187 14.66ZM42.1987 12.22C42.692 12.22 43.1253 12.1133 43.4987 11.9C43.8853 11.6733 44.1787 11.3666 44.3787 10.98C44.592 10.58 44.6987 10.1266 44.6987 9.61996C44.6987 9.11329 44.592 8.66663 44.3787 8.27996C44.1653 7.89329 43.872 7.59329 43.4987 7.37996C43.1253 7.15329 42.692 7.03996 42.1987 7.03996C41.7187 7.03996 41.292 7.15329 40.9187 7.37996C40.5453 7.59329 40.252 7.89329 40.0387 8.27996C39.8253 8.66663 39.7187 9.11329 39.7187 9.61996C39.7187 10.1266 39.8253 10.58 40.0387 10.98C40.252 11.3666 40.5453 11.6733 40.9187 11.9C41.292 12.1133 41.7187 12.22 42.1987 12.22ZM55.9654 14.66C54.9387 14.66 53.9854 14.48 53.1054 14.12C52.2254 13.7466 51.452 13.2333 50.7854 12.58C50.132 11.9133 49.6254 11.14 49.2654 10.26C48.9187 9.37996 48.7454 8.43329 48.7454 7.41996C48.7454 6.40663 48.9187 5.45996 49.2654 4.57996C49.6254 3.69996 50.132 2.93329 50.7854 2.27996C51.452 1.62663 52.2187 1.11329 53.0854 0.73996C53.9654 0.366627 54.9254 0.17996 55.9654 0.17996C57.0854 0.17996 58.072 0.366627 58.9254 0.73996C59.7787 1.09996 60.532 1.59996 61.1854 2.23996L59.3454 4.07996C58.9587 3.65329 58.4854 3.31996 57.9254 3.07996C57.3654 2.83996 56.712 2.71996 55.9654 2.71996C55.312 2.71996 54.712 2.83329 54.1654 3.05996C53.632 3.27329 53.1654 3.59329 52.7654 4.01996C52.3654 4.43329 52.0587 4.93329 51.8454 5.51996C51.632 6.09329 51.5254 6.72663 51.5254 7.41996C51.5254 8.12663 51.632 8.76663 51.8454 9.33996C52.0587 9.91329 52.3654 10.4133 52.7654 10.84C53.1654 11.2533 53.632 11.5733 54.1654 11.8C54.712 12.0266 55.312 12.14 55.9654 12.14C56.752 12.14 57.4254 12.02 57.9854 11.78C58.5587 11.54 59.0387 11.2 59.4254 10.76L61.2654 12.6C60.612 13.2533 59.852 13.76 58.9854 14.12C58.1187 14.48 57.112 14.66 55.9654 14.66ZM67.0963 14.66C66.243 14.66 65.483 14.48 64.8163 14.12C64.163 13.76 63.6496 13.26 63.2763 12.62C62.903 11.98 62.7163 11.24 62.7163 10.4V4.79996H65.3563V10.34C65.3563 10.74 65.423 11.0866 65.5563 11.38C65.6896 11.66 65.8896 11.8733 66.1563 12.02C66.423 12.1666 66.7363 12.24 67.0963 12.24C67.643 12.24 68.0696 12.0733 68.3763 11.74C68.683 11.4066 68.8363 10.94 68.8363 10.34V4.79996H71.4563V10.4C71.4563 11.2533 71.2696 12 70.8963 12.64C70.5363 13.28 70.023 13.78 69.3563 14.14C68.703 14.4866 67.9496 14.66 67.0963 14.66ZM75.0627 14.46V0.779961H77.6827V14.46H75.0627ZM72.8027 7.09996V4.79996H79.9427V7.09996H72.8027Z"
              fill="white"
            />
          </svg>
        </Link>
        <nav className={styles.nav}>
          <Dropdown
            menu={{ items: featureMenuItems, className: styles.navDropdownMenu }}
            trigger={['hover']}
            placement="bottomLeft"
            overlayClassName={styles.navDropdown}
          >
            <button type="button" className={styles.navDropdownTrigger}>
              Features
              <DownOutlined />
            </button>
          </Dropdown>
          <Link href={viralTemplateURL}>Templates</Link>
          <Dropdown
            menu={{ items: useCaseMenuItems, className: styles.navDropdownMenu }}
            trigger={['hover']}
            placement="bottomLeft"
            overlayClassName={styles.navDropdown}
          >
            <button type="button" className={styles.navDropdownTrigger}>
              Use Cases
              <DownOutlined />
            </button>
          </Dropdown>
          <Link href={pricingURL}>Pricing</Link>
        </nav>
        <div className={styles.headerActions}>
          <Link href={HOME_CTA_URL} className={styles.headerCta}>
            Start Creating
          </Link>
          <button
            type="button"
            className={styles.mobileButton}
            onClick={() => setMobileOpen((open) => !open)}
            aria-label="Toggle navigation"
          >
            <MenuOutlined />
          </button>
        </div>
      </div>
      {mobileOpen && (
        <div className={styles.mobileMenu}>
          <div className={styles.mobileMenuBlock}>
            <button
              type="button"
              className={styles.mobileMenuRow}
              onClick={() =>
                setExpandedMobile((prev) => (prev === 'features' ? null : 'features'))
              }
            >
              Features
              <span>{expandedMobile === 'features' ? '−' : '+'}</span>
            </button>
            {expandedMobile === 'features' &&
              NEW_HOME_FEATURE_GROUPS.map((group) => (
                <div key={group.key} className={styles.mobileMenuGroup}>
                  <div className={styles.mobileMenuGroupTitle}>{group.label}</div>
                  {group.links.map((link) => (
                    <Link
                      key={link.key}
                      href={link.href}
                      className={styles.mobileMenuLink}
                      onClick={closeMobile}
                    >
                      {link.label}
                    </Link>
                  ))}
                </div>
              ))}
          </div>
          <Link
            href={viralTemplateURL}
            className={styles.mobileMenuRowLink}
            onClick={closeMobile}
          >
            Templates
          </Link>
          <div className={styles.mobileMenuBlock}>
            <button
              type="button"
              className={styles.mobileMenuRow}
              onClick={() =>
                setExpandedMobile((prev) => (prev === 'useCases' ? null : 'useCases'))
              }
            >
              Use Cases
              <span>{expandedMobile === 'useCases' ? '−' : '+'}</span>
            </button>
            {expandedMobile === 'useCases' &&
              NEW_HOME_USE_CASE_LINKS.map((link) => (
                <Link
                  key={link.key}
                  href={link.href}
                  className={styles.mobileMenuLink}
                  onClick={closeMobile}
                >
                  {link.label}
                </Link>
              ))}
          </div>
          <Link
            href={pricingURL}
            className={styles.mobileMenuRowLink}
            onClick={closeMobile}
          >
            Pricing
          </Link>
        </div>
      )}
    </header>
  );
};

const HeroSection = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [visitedTabs, setVisitedTabs] = useState<number[]>([0]);
  const [unmutedSlotKey, setUnmutedSlotKey] = useState<string | null>(null);
  const [avatarHero, setAvatarHero] = useState<HeroVideoItem[]>([]);
  const avatarFetchedRef = useRef(false);
  const tabRefs = useRef<Array<HTMLAnchorElement | null>>([]);
  const [indicatorOffset, setIndicatorOffset] = useState({ width: 168, x: 7 });
  const { templates: sharedTemplates } = useSharedTemplateList();

  const templateHero = useMemo(
    () =>
      sharedTemplates.length > 0
        ? mapTemplateVideoItemsToHeroVideos(sharedTemplates)
        : [],
    [sharedTemplates],
  );

  useEffect(() => {
    setUnmutedSlotKey(null);
    setVisitedTabs((prev) => (prev.includes(activeTab) ? prev : [...prev, activeTab]));
  }, [activeTab]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setActiveTab((prev) => (prev + 1) % heroTabs.length);
    }, 10000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    const updateIndicator = () => {
      const activeNode = tabRefs.current[activeTab];
      if (!activeNode) return;
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      setIndicatorOffset({
        width: activeNode.offsetWidth,
        x: activeNode.offsetLeft,
      });
      activeNode.scrollIntoView({
        block: 'nearest',
        inline: 'nearest',
        behavior: reduceMotion ? 'auto' : 'smooth',
      });
    };

    updateIndicator();
    window.addEventListener('resize', updateIndicator);
    return () => window.removeEventListener('resize', updateIndicator);
  }, [activeTab]);

  useEffect(() => {
    if (activeTab !== 1 || avatarFetchedRef.current) return;
    const ac = new AbortController();
    void (async () => {
      try {
        const r = await fetch('/api/landing/avatar-hero-videos', { signal: ac.signal });
        if (ac.signal.aborted) return;
        const j = (await r.json()) as {
          items?: Array<{ id: string; label: string; poster: string; videoUrl: string }>;
        };
        if (ac.signal.aborted) return;
        if (!j.items?.length) return;
        setAvatarHero(
          j.items.map((row) => ({
            id: row.id,
            label: row.label,
            poster: row.poster || row.videoUrl,
            videoUrl: row.videoUrl,
          })),
        );
        avatarFetchedRef.current = true;
      } catch (e: unknown) {
        if (e instanceof DOMException && e.name === 'AbortError') return;
      }
    })();
    return () => ac.abort();
  }, [activeTab]);

  const getMarqueeListByTab = (tabIndex: number) => {
    if (tabIndex === 0) {
      return templateHero.length > 0 ? templateHero : heroVideos;
    }
    if (tabIndex === 1) {
      return avatarHero.length > 0 ? avatarHero : heroVideos;
    }
    return AGENT_GENERATE_HERO_ITEMS;
  };
  const renderedTabs = visitedTabs.includes(activeTab)
    ? visitedTabs
    : [...visitedTabs, activeTab];

  return (
    <section className={styles.hero}>
      <div className={styles.glowOne} />
      <div className={styles.glowTwo} />
      <div className={styles.heroContent}>
        <div className={styles.heroCopy}>
          <h1>
            <span>Go Viral Tonight</span>
            <span>
              High-Converting <em>AI</em>
            </span>
          </h1>
          <p>1 Click. 1M Views. 0 Skills Required.</p>
        </div>
        <Link href={HOME_CTA_URL} className={styles.primaryCta}>
          Get 1M Views Now
          <ArrowIcon />
        </Link>
        <div
          className={styles.segmented}
          style={
            {
              '--segmented-indicator-width': `${indicatorOffset.width}px`,
              '--segmented-indicator-x': `${indicatorOffset.x}px`,
            } as CSSProperties
          }
        >
          <span className={styles.segmentedIndicator} aria-hidden="true" />
          {heroTabs.map((tab, index) => (
            <Link
              key={tab.label}
              ref={(node) => {
                tabRefs.current[index] = node;
              }}
              href={tab.href}
              className={index === activeTab ? styles.activeTab : ''}
            >
              <span>{tab.icon as React.ReactNode}</span>
              {tab.label}
            </Link>
          ))}
        </div>
        <div className={styles.heroMarquee}>
          {heroTabs.map((tab, tabIndex) => {
            if (!renderedTabs.includes(tabIndex)) return null;
            const isActivePanel = activeTab === tabIndex;
            const panelClassName = `${styles.heroPanel} ${
              isActivePanel ? styles.heroPanelActive : styles.heroPanelHidden
            }`;

            if (tabIndex === 3) {
              return (
                <div key={tab.label} className={panelClassName}>
                  <HeroAIToolsStrip />
                </div>
              );
            }

            const marqueeList = getMarqueeListByTab(tabIndex);
            const displayList = marqueeList.length > 0 ? marqueeList : heroVideos;

            return (
              <div key={tab.label} className={panelClassName}>
                <div className={styles.heroTrack}>
                  {[...displayList, ...displayList].map((item, index) => {
                    const slotKey = `hero-t${tabIndex}-${item.id}-${index}`;
                    return (
                      <HeroMarqueeVideoCard
                        key={slotKey}
                        item={item}
                        slotKey={slotKey}
                        isActive={isActivePanel}
                        isUnmuted={isActivePanel && unmutedSlotKey === slotKey}
                        onToggleSound={() => {
                          setUnmutedSlotKey((prev) =>
                            prev === slotKey ? null : slotKey,
                          );
                        }}
                      />
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

const socialLink = [
  // {
  //   link: 'https://www.youtube.com/@FalcoCut',
  //   icon: (
  //     <svg
  //       xmlns="http://www.w3.org/2000/svg"
  //       width="24"
  //       height="24"
  //       viewBox="0 0 24 24"
  //       fill="none"
  //     >
  //       <path
  //         d="M5.53185 5.2072C8.30345 5.06905 10.4591 5 11.999 5C13.539 5 15.6955 5.0691 18.4685 5.20725C19.9695 5.28205 21.1836 6.45595 21.3088 7.9536C21.4356 9.4706 21.499 10.8067 21.499 11.9619C21.499 13.1313 21.434 14.486 21.3041 16.0261C21.1793 17.5052 19.9916 18.6706 18.5105 18.7675C16.1398 18.9225 13.9693 19 11.999 19C10.029 19 7.8593 18.9225 5.4899 18.7675C4.00926 18.6707 2.82185 17.506 2.69644 16.0275C2.56483 14.4759 2.49902 13.1207 2.49902 11.9619C2.49902 10.817 2.56325 9.4805 2.69172 7.9522C2.81756 6.45515 4.03137 5.282 5.53185 5.2072Z"
  //         stroke="white"
  //         stroke-width="2"
  //         stroke-linejoin="round"
  //       />
  //       <path
  //         d="M10.5 9.8046V14.203C10.5 14.4397 10.6919 14.6316 10.9285 14.6316C11.0126 14.6316 11.0948 14.6069 11.1649 14.5606L14.4637 12.3803C14.6612 12.2498 14.7154 11.9839 14.5849 11.7864C14.5537 11.7392 14.5135 11.6987 14.4665 11.6671L11.1677 9.44895C10.9713 9.31685 10.705 9.36905 10.5729 9.56545C10.5254 9.63615 10.5 9.7194 10.5 9.8046Z"
  //         stroke="white"
  //         stroke-width="2"
  //         stroke-linejoin="round"
  //       />
  //     </svg>
  //   ),
  // },
  {
    link: 'https://www.instagram.com/falcocutai_official/',
    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
      >
        <path
          d="M17 3H7C4.79086 3 3 4.79086 3 7V17C3 19.2092 4.79086 21 7 21H17C19.2092 21 21 19.2092 21 17V7C21 4.79086 19.2092 3 17 3Z"
          stroke="white"
          stroke-width="2"
          stroke-linejoin="round"
        />
        <path
          d="M12 16C14.2092 16 16 14.2092 16 12C16 9.79085 14.2092 8 12 8C9.79085 8 8 9.79085 8 12C8 14.2092 9.79085 16 12 16Z"
          stroke="white"
          stroke-width="2"
          stroke-linejoin="round"
        />
        <path
          d="M17.5 7.5C18.0523 7.5 18.5 7.0523 18.5 6.5C18.5 5.9477 18.0523 5.5 17.5 5.5C16.9477 5.5 16.5 5.9477 16.5 6.5C16.5 7.0523 16.9477 7.5 17.5 7.5Z"
          fill="white"
        />
      </svg>
    ),
  },
  {
    link: 'https://www.tiktok.com/@falcocutai',
    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
      >
        <path
          d="M10.679 9.57032C7.7347 9.42827 5.6881 10.478 4.53919 12.7196C2.81585 16.0819 4.24013 21.5837 9.9894 21.5837C15.7387 21.5837 15.905 16.0281 15.905 15.4461C15.905 15.0581 15.905 12.8886 15.905 8.93767C17.1347 9.71652 18.1715 10.1854 19.0154 10.3442C19.8593 10.503 20.3958 10.5734 20.6249 10.5554V7.31752C19.8443 7.22342 19.1693 7.04402 18.5999 6.77942C17.7457 6.38252 16.0519 5.28092 16.0519 3.66641C16.053 3.6743 16.053 3.25783 16.0519 2.41699H12.4929C12.4823 10.325 12.4823 14.668 12.4929 15.4461C12.5088 16.6131 11.6034 18.2456 9.76775 18.2456C7.9321 18.2456 7.02675 16.6144 7.02675 15.5623C7.02675 14.9182 7.248 13.9846 8.16255 13.2933C8.7049 12.8833 9.45765 12.7196 10.679 12.7196C10.679 12.3418 10.679 11.292 10.679 9.57032Z"
          fill="white"
          stroke="white"
          stroke-width="0.5"
          stroke-linejoin="round"
        />
      </svg>
    ),
  },
  {
    link: 'https://x.com/falcocut_ai',
    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="18"
        height="19"
        viewBox="0 0 18 19"
        fill="none"
      >
        <g clip-path="url(#clip0_2099_14010)">
          <path
            d="M10.7124 7.78926L17.4133 0H15.8254L10.0071 6.7633L5.35992 0H0L7.02738 10.2273L0 18.3955H1.58799L7.73237 11.2533L12.6401 18.3955H18L10.7121 7.78926H10.7124ZM8.53747 10.3174L7.82546 9.29901L2.16017 1.19542H4.59922L9.17118 7.73527L9.8832 8.75368L15.8262 17.2545H13.3871L8.53747 10.3178V10.3174Z"
            fill="white"
          />
        </g>
        <defs>
          <clipPath id="clip0_2099_14010">
            <rect width="18" height="18.405" fill="white" />
          </clipPath>
        </defs>
      </svg>
    ),
  },
];

const HomeFooter = () => (
  <footer className={styles.footer}>
    <div className={styles.footerPanel}>
      <div className={styles.footerLinks}>
        {footerSections.map((section) => (
          <div key={section.title}>
            <h3>{section.title}</h3>
            {section.links.map((link) => (
              <Link key={link.text} href={link.href}>
                {link.text}
              </Link>
            ))}
          </div>
        ))}
      </div>
      <div className={styles.footerBottom}>
        <div className="flex">
          <div className={styles.footerBrand}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="40"
              height="40"
              viewBox="0 0 40 40"
              fill="none"
            >
              <path
                d="M12.4347 12.2295C12.4347 8.51561 15.4455 5.50488 19.1594 5.50488H39.3333C39.3333 9.2188 36.3226 12.2295 32.6087 12.2295H12.4347Z"
                fill="url(#paint0_linear_2099_13995)"
              />
              <path
                d="M0.666626 21.4759C0.666626 17.762 3.67735 14.7513 7.39127 14.7513H27.5652C27.5652 18.4652 24.5545 21.4759 20.8405 21.4759H0.666626Z"
                fill="url(#paint1_linear_2099_13995)"
              />
              <path
                d="M15.0637 29.3179C14.2083 32.932 10.5854 35.1681 6.9713 34.3127L9.4127 23.9976H16.3229L15.0637 29.3179Z"
                fill="url(#paint2_linear_2099_13995)"
              />
              <defs>
                <linearGradient
                  id="paint0_linear_2099_13995"
                  x1="8.81852"
                  y1="34.0231"
                  x2="32.4578"
                  y2="15.0306"
                  gradientUnits="userSpaceOnUse"
                >
                  <stop stop-color="#8F49FF" />
                  <stop offset="1" stop-color="#3CC2FF" />
                </linearGradient>
                <linearGradient
                  id="paint1_linear_2099_13995"
                  x1="8.81852"
                  y1="34.0231"
                  x2="32.4578"
                  y2="15.0306"
                  gradientUnits="userSpaceOnUse"
                >
                  <stop stop-color="#8F49FF" />
                  <stop offset="1" stop-color="#3CC2FF" />
                </linearGradient>
                <linearGradient
                  id="paint2_linear_2099_13995"
                  x1="8.81852"
                  y1="34.0231"
                  x2="32.4578"
                  y2="15.0306"
                  gradientUnits="userSpaceOnUse"
                >
                  <stop stop-color="#8F49FF" />
                  <stop offset="1" stop-color="#3CC2FF" />
                </linearGradient>
              </defs>
            </svg>
            <svg
              width="80"
              height="15"
              viewBox="0 0 80 15"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="scale-125 ml-[10px]"
            >
              <path
                d="M5.86808e-05 14.46V0.399961H2.72006V14.46H5.86808e-05ZM2.00006 8.69996V6.27996H9.08006V8.69996H2.00006ZM2.00006 2.79996V0.399961H9.42006V2.79996H2.00006ZM15.0905 14.66C14.2105 14.66 13.4172 14.44 12.7105 14C12.0039 13.56 11.4505 12.96 11.0505 12.2C10.6505 11.44 10.4505 10.5866 10.4505 9.63996C10.4505 8.67996 10.6505 7.81996 11.0505 7.05996C11.4505 6.29996 12.0039 5.69996 12.7105 5.25996C13.4172 4.81996 14.2105 4.59996 15.0905 4.59996C15.7839 4.59996 16.4039 4.73996 16.9505 5.01996C17.4972 5.29996 17.9305 5.69329 18.2505 6.19996C18.5839 6.69329 18.7639 7.25329 18.7905 7.87996V11.36C18.7639 12 18.5839 12.5666 18.2505 13.06C17.9305 13.5533 17.4972 13.9466 16.9505 14.24C16.4039 14.52 15.7839 14.66 15.0905 14.66ZM15.5705 12.24C16.3039 12.24 16.8972 12 17.3505 11.52C17.8039 11.0266 18.0305 10.3933 18.0305 9.61996C18.0305 9.11329 17.9239 8.66663 17.7105 8.27996C17.5105 7.87996 17.2239 7.57329 16.8505 7.35996C16.4905 7.13329 16.0639 7.01996 15.5705 7.01996C15.0905 7.01996 14.6639 7.13329 14.2905 7.35996C13.9305 7.57329 13.6439 7.87996 13.4305 8.27996C13.2305 8.66663 13.1305 9.11329 13.1305 9.61996C13.1305 10.14 13.2305 10.6 13.4305 11C13.6439 11.3866 13.9305 11.6933 14.2905 11.92C14.6639 12.1333 15.0905 12.24 15.5705 12.24ZM17.8905 14.46V11.86L18.3105 9.49996L17.8905 7.17996V4.79996H20.4905V14.46H17.8905ZM22.9083 14.46V-4.00543e-05H25.5283V14.46H22.9083ZM32.5097 14.66C31.5231 14.66 30.6364 14.44 29.8497 14C29.0631 13.56 28.4431 12.96 27.9897 12.2C27.5364 11.44 27.3097 10.5866 27.3097 9.63996C27.3097 8.67996 27.5364 7.81996 27.9897 7.05996C28.4431 6.29996 29.0631 5.69996 29.8497 5.25996C30.6497 4.80663 31.5364 4.57996 32.5097 4.57996C33.2697 4.57996 33.9697 4.71996 34.6097 4.99996C35.2497 5.27996 35.8031 5.67996 36.2697 6.19996L34.5897 7.89996C34.3364 7.61996 34.0297 7.40663 33.6697 7.25996C33.3231 7.11329 32.9364 7.03996 32.5097 7.03996C32.0297 7.03996 31.5964 7.15329 31.2097 7.37996C30.8364 7.59329 30.5364 7.89329 30.3097 8.27996C30.0964 8.65329 29.9897 9.09996 29.9897 9.61996C29.9897 10.1266 30.0964 10.58 30.3097 10.98C30.5364 11.3666 30.8364 11.6733 31.2097 11.9C31.5964 12.1133 32.0297 12.22 32.5097 12.22C32.9497 12.22 33.3431 12.1466 33.6897 12C34.0497 11.84 34.3564 11.6133 34.6097 11.32L36.3097 13.02C35.8164 13.5533 35.2497 13.96 34.6097 14.24C33.9697 14.52 33.2697 14.66 32.5097 14.66ZM42.2187 14.66C41.2453 14.66 40.3653 14.44 39.5787 14C38.8053 13.5466 38.1853 12.94 37.7187 12.18C37.2653 11.4066 37.0387 10.5466 37.0387 9.59996C37.0387 8.65329 37.2653 7.80663 37.7187 7.05996C38.172 6.29996 38.792 5.69996 39.5787 5.25996C40.3653 4.80663 41.2387 4.57996 42.1987 4.57996C43.1853 4.57996 44.0653 4.80663 44.8387 5.25996C45.6253 5.69996 46.2453 6.29996 46.6987 7.05996C47.152 7.80663 47.3787 8.65329 47.3787 9.59996C47.3787 10.5466 47.152 11.4066 46.6987 12.18C46.2453 12.94 45.6253 13.5466 44.8387 14C44.0653 14.44 43.192 14.66 42.2187 14.66ZM42.1987 12.22C42.692 12.22 43.1253 12.1133 43.4987 11.9C43.8853 11.6733 44.1787 11.3666 44.3787 10.98C44.592 10.58 44.6987 10.1266 44.6987 9.61996C44.6987 9.11329 44.592 8.66663 44.3787 8.27996C44.1653 7.89329 43.872 7.59329 43.4987 7.37996C43.1253 7.15329 42.692 7.03996 42.1987 7.03996C41.7187 7.03996 41.292 7.15329 40.9187 7.37996C40.5453 7.59329 40.252 7.89329 40.0387 8.27996C39.8253 8.66663 39.7187 9.11329 39.7187 9.61996C39.7187 10.1266 39.8253 10.58 40.0387 10.98C40.252 11.3666 40.5453 11.6733 40.9187 11.9C41.292 12.1133 41.7187 12.22 42.1987 12.22ZM55.9654 14.66C54.9387 14.66 53.9854 14.48 53.1054 14.12C52.2254 13.7466 51.452 13.2333 50.7854 12.58C50.132 11.9133 49.6254 11.14 49.2654 10.26C48.9187 9.37996 48.7454 8.43329 48.7454 7.41996C48.7454 6.40663 48.9187 5.45996 49.2654 4.57996C49.6254 3.69996 50.132 2.93329 50.7854 2.27996C51.452 1.62663 52.2187 1.11329 53.0854 0.73996C53.9654 0.366627 54.9254 0.17996 55.9654 0.17996C57.0854 0.17996 58.072 0.366627 58.9254 0.73996C59.7787 1.09996 60.532 1.59996 61.1854 2.23996L59.3454 4.07996C58.9587 3.65329 58.4854 3.31996 57.9254 3.07996C57.3654 2.83996 56.712 2.71996 55.9654 2.71996C55.312 2.71996 54.712 2.83329 54.1654 3.05996C53.632 3.27329 53.1654 3.59329 52.7654 4.01996C52.3654 4.43329 52.0587 4.93329 51.8454 5.51996C51.632 6.09329 51.5254 6.72663 51.5254 7.41996C51.5254 8.12663 51.632 8.76663 51.8454 9.33996C52.0587 9.91329 52.3654 10.4133 52.7654 10.84C53.1654 11.2533 53.632 11.5733 54.1654 11.8C54.712 12.0266 55.312 12.14 55.9654 12.14C56.752 12.14 57.4254 12.02 57.9854 11.78C58.5587 11.54 59.0387 11.2 59.4254 10.76L61.2654 12.6C60.612 13.2533 59.852 13.76 58.9854 14.12C58.1187 14.48 57.112 14.66 55.9654 14.66ZM67.0963 14.66C66.243 14.66 65.483 14.48 64.8163 14.12C64.163 13.76 63.6496 13.26 63.2763 12.62C62.903 11.98 62.7163 11.24 62.7163 10.4V4.79996H65.3563V10.34C65.3563 10.74 65.423 11.0866 65.5563 11.38C65.6896 11.66 65.8896 11.8733 66.1563 12.02C66.423 12.1666 66.7363 12.24 67.0963 12.24C67.643 12.24 68.0696 12.0733 68.3763 11.74C68.683 11.4066 68.8363 10.94 68.8363 10.34V4.79996H71.4563V10.4C71.4563 11.2533 71.2696 12 70.8963 12.64C70.5363 13.28 70.023 13.78 69.3563 14.14C68.703 14.4866 67.9496 14.66 67.0963 14.66ZM75.0627 14.46V0.779961H77.6827V14.46H75.0627ZM72.8027 7.09996V4.79996H79.9427V7.09996H72.8027Z"
                fill="white"
              />
            </svg>
          </div>
          <div className="flex items-center gap-2 mx-[28px]">
            {socialLink.map((item) => {
              return (
                <span
                  key={item.link}
                  className="cursor-pointer"
                  onClick={() => window.open(item.link, '_blank')}
                >
                  {item.icon}
                </span>
              );
            })}
          </div>
        </div>
        <p>Get videos for markets around the world.</p>
        <small>@ 2026 FalcoCut</small>
      </div>
    </div>
  </footer>
);

const NewHomePage: NextPage & { isLandingPage?: boolean } = () => {
  return (
    <>
      <SEOHead {...SEO_CONFIG} />
      <div className={styles.page}>
        <HomeHeader />
        <NewHomeTemplateListProvider>
          <LandingBridgeProvider>
            <main>
              <HeroSection />
              <LogosSection />
              <TemplatesSection />
              <ToolsTicker />
              <StepsSection />
              <FeatureBlocks />
              <AvatarShowcase />
              <ToolCardsSection />
              <TestimonialsSection />
              <FAQSection />
            </main>
          </LandingBridgeProvider>
        </NewHomeTemplateListProvider>
        <HomeFooter />
      </div>
    </>
  );
};

NewHomePage.isLandingPage = true;

export default NewHomePage;
